import { clsx } from "clsx";

interface SkeletonProps {
  className?: string;
}

export const Skeleton = ({ className }: SkeletonProps) => (
  <div
    aria-hidden="true"
    className={clsx("animate-pulse rounded-md bg-slate-200", className)}
  />
);

interface SkeletonTextProps {
  lines?: number;
  className?: string;
}

export const SkeletonText = ({ lines = 3, className }: SkeletonTextProps) => (
  <div className={clsx("space-y-2", className)} aria-hidden="true">
    {Array.from({ length: lines }).map((_, idx) => {
      const isLast = idx === lines - 1;
      return (
        <Skeleton
          key={idx}
          className={clsx("h-3 w-full", isLast && lines > 1 ? "w-3/4" : "")}
        />
      );
    })}
  </div>
);

interface SkeletonCardProps {
  className?: string;
}

export const SkeletonCard = ({ className }: SkeletonCardProps) => (
  <div
    className={clsx(
      "rounded-lg border border-slate-200 bg-white p-4 shadow-sm",
      className,
    )}
    aria-hidden="true"
  >
    <div className="flex items-center gap-3">
      <Skeleton className="h-10 w-10 rounded-full" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-3 w-1/3" />
        <Skeleton className="h-3 w-1/2" />
      </div>
    </div>
    <div className="mt-4">
      <SkeletonText lines={3} />
    </div>
  </div>
);

interface SkeletonTableProps {
  rows?: number;
  cols?: number;
  className?: string;
}

export const SkeletonTable = ({
  rows = 5,
  cols = 4,
  className,
}: SkeletonTableProps) => (
  <div
    className={clsx("w-full overflow-hidden rounded-lg border border-slate-200", className)}
    aria-hidden="true"
  >
    <div className="grid border-b border-slate-200 bg-slate-50 px-4 py-3"
         style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
      {Array.from({ length: cols }).map((_, idx) => (
        <Skeleton key={`h-${idx}`} className="h-3 w-2/3" />
      ))}
    </div>
    <div className="divide-y divide-slate-200">
      {Array.from({ length: rows }).map((_, rowIdx) => (
        <div
          key={`r-${rowIdx}`}
          className="grid px-4 py-3"
          style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
        >
          {Array.from({ length: cols }).map((__, colIdx) => (
            <Skeleton
              key={`c-${rowIdx}-${colIdx}`}
              className={clsx("h-3", colIdx === 0 ? "w-3/4" : "w-2/3")}
            />
          ))}
        </div>
      ))}
    </div>
  </div>
);
